/**
 * Pasek trwającej sesji wsparcia.
 *
 * Gdy partner wejdzie na konto klienta, przez cały czas widzi u góry ekranu,
 * CZYJE to konto, ile czasu zostało i że sesja jest nagrywana. To nie jest
 * ozdoba: bez tego paska partner po chwili nie odróżnia cudzego konta od
 * swojego panelu, a zgoda najemcy dotyczyła świadomej, ograniczonej wizyty.
 *
 * Pasek jest też miejscem, w którym nagrywana jest NAWIGACJA — każda zmiana
 * trasy dopisuje wpis do dziennika sesji. Robi to komponent, a nie router,
 * bo tylko tutaj wiadomo, że sesja w ogóle trwa.
 */

import { LogOut, Radio, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  biegnacaSesja,
  nagrajZdarzenie,
  subskrybujSesje,
  zakonczSesje,
  type AktywnaSesjaWsparcia,
} from '../../lib/supportSession';

function pozostaloMinut(expiresAt: string): number {
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.now()) / 60_000));
}

export function SupportSessionBanner() {
  const location = useLocation();
  const [sesja, setSesja] = useState<AktywnaSesjaWsparcia | null>(() => biegnacaSesja());
  const [minuty, setMinuty] = useState(() => (sesja ? pozostaloMinut(sesja.expiresAt) : 0));
  const [konczy, setKonczy] = useState(false);

  useEffect(() => subskrybujSesje(setSesja), []);

  // Odliczanie — po wygaśnięciu `biegnacaSesja()` sama zwróci null i pasek znika.
  useEffect(() => {
    if (!sesja) return;
    const t = setInterval(() => {
      const zostalo = pozostaloMinut(sesja.expiresAt);
      setMinuty(zostalo);
      if (zostalo <= 0) setSesja(biegnacaSesja());
    }, 30_000);
    setMinuty(pozostaloMinut(sesja.expiresAt));
    return () => clearInterval(t);
  }, [sesja]);

  // Nagrywanie nawigacji.
  useEffect(() => {
    if (!sesja) return;
    void nagrajZdarzenie({
      kind: 'nawigacja',
      route: location.pathname + (location.search || ''),
      action: 'otwarcie_ekranu',
      summary: `Otwarcie ekranu ${location.pathname}`,
    });
  }, [sesja, location.pathname, location.search]);

  // Wejście serwisowe super-admina (837) — bez kodu, z powodem.
  const serwis = sesja?.rodzaj === 'serwis';

  const zakoncz = useCallback(async () => {
    setKonczy(true);
    try {
      await zakonczSesje(serwis ? 'admin' : 'partner');
    } finally {
      setKonczy(false);
      // Konto klienta znika razem z członkostwem — powrót do własnego panelu:
      // super-admin po wejściu serwisowym wraca do listy organizacji.
      window.location.href = serwis ? '/super-admin/tenants' : '/partner';
    }
  }, [serwis]);

  if (!sesja) return null;

  return (
    <div className="sticky top-0 z-50 bg-gradient-to-r from-amber-600/90 to-red-600/90 backdrop-blur text-aurora-on-accent">
      <div className="px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span>
            Jesteś na koncie klienta <strong>{sesja.organizationName}</strong>
            {sesja.scope === 'tylko_odczyt' ? ' (tylko podgląd)' : ''}
            {serwis ? ' — wejście serwisowe Insolitum.' : ' — za jego zgodą.'}
          </span>
          <span className="hidden sm:inline-flex items-center gap-1 text-aurora-text/90">
            <Radio className="w-3.5 h-3.5 animate-pulse" />
            sesja nagrywana
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-aurora-text/90">pozostało ~{minuty} min</span>
          <button
            onClick={zakoncz}
            disabled={konczy}
            className="inline-flex items-center gap-1 px-3 py-1 bg-black/30 rounded-ids-md hover:bg-black/50 disabled:opacity-50"
          >
            <LogOut className="w-3.5 h-3.5" />
            Zakończ sesję
          </button>
        </div>
      </div>
    </div>
  );
}

export default SupportSessionBanner;
